// TODO: Properly type this file: https://github.com/webflow/webflow/issues/26407
// CMS WHTML serialization for Component Instances: `<wf-component>` emit + parse-resolve. The RT renderer, the parser deps, and error reporting are all injected so this package never depends on JsonDomUtils or the heavy html-to-webflow / corePlugins server chains (avoids a dependency cycle).
import type {JSONNode} from '@packages/shared/types/JSONNode';
import type {AnnotationCtx as WhtmlAnnotationCtx} from './annotationCtx';
import {exportElementAsJSONNodeArray} from '@packages/systems/tree';
import * as GuidUtils from '@packages/utilities/guid';
import htmlparser2 from 'htmlparser2';
import type {ExpressionElement} from '@packages/systems/wfdl-ast';
import type {Component} from '@packages/systems/wfdl';
import {
  containsRichTextSlotOverride,
  findRichTextForbiddenElementInComponent,
  getRichTextForbiddenElementDisplayName,
} from '@packages/utilities/rich-text-validation';
// `import type` only — the runtime values are injected via `parserDeps` (see
// `buildComponentInstanceJsonNodes`), so there is no static edge to these packages.
import type {
  HTMLToWFDLParser as HTMLToWFDLParserType,
  ComponentPropParseError,
} from '@packages/systems/html-to-webflow';
import type {getBootstrappedPlugins as GetBootstrappedPluginsType} from '@packages/shared/render/corePlugins';

// WHTML attr escapers, mirroring the parser's htmlparser2 `decodeEntities`.
export const escWhtmlAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/"/g, '&quot;');
export const escWhtmlSingleQuotedAttr = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/'/g, '&#39;');

/** Emit a Component Instance straight to `<wf-component>` (no reification); overrides classified by stored shape. Failures fall back to an empty tag keeping identity. */
export const emitComponentInstanceWhtmlMarkup = (opts: {
  instanceNode: JSONNode;
  nodeIndex: Record<string, JSONNode>;
  // Injected RT renderer (avoids a cycle with `rteJsonNodesToHtml`).
  renderNodes: (nodes: JSONNode[]) => string;
  // Optional `name=`/`label=` annotations (decorative; ignored on parse).
  annotate?: WhtmlAnnotationCtx;
  // Injected error reporter (runs server-side); the caller wires logging + a
  // counter. `error` carries the caught throwable so the caller can preserve
  // the stack (it's absent for non-throw reports like a partial node-ref array).
  onError?: (info: {
    instanceId: string;
    message: string;
    error?: unknown;
  }) => void;
}): string => {
  const {instanceNode, nodeIndex, renderNodes, annotate, onError} = opts;
  const typeId = instanceNode.data?.sym?.inst ?? '';
  try {
    const overrides = instanceNode.data?.sym?.overrides ?? {};
    const componentNameAttr =
      annotate && typeId
        ? (() => {
            const name = annotate.resolveComponentName(typeId);
            return name ? ` name="${escWhtmlAttr(name)}"` : '';
          })()
        : '';

    // Collect a node + its descendants in document order. Follows both
    // `children` and Component Instance override references: a nested instance
    // stores its RT/Plain-Text override bodies as node-ref arrays in
    // `data.sym.overrides` (the override nodes live outside `children`, the
    // same way `rootIds` treats them as the instance's children). Without
    // following them the recursive `renderNodes` index would be missing the
    // nested instance's override nodes, so it would re-emit as an empty
    // `<wf-component>` and drop its props.
    const collectSubtree = (rootId: string): JSONNode[] => {
      const out: JSONNode[] = [];
      const seen = new Set<string>();
      const visit = (id: string) => {
        if (seen.has(id)) return;
        const n = nodeIndex[id];
        if (!n) return;
        seen.add(id);
        out.push(n);
        if (n.children) for (const childId of n.children) visit(childId);
        const nestedOverrides = n.data?.sym?.overrides;
        if (nestedOverrides) {
          for (const value of Object.values(nestedOverrides)) {
            // Node-ref body: a non-empty array of id strings (same shape the
            // emitter classifies as RT/Plain-Text below). Attributes overrides
            // are arrays of `{name, value}` objects, so they're skipped here.
            if (
              Array.isArray(value) &&
              value.length > 0 &&
              value.every((v) => typeof v === 'string')
            ) {
              for (const refId of value) {
                if (refId in nodeIndex) visit(refId);
              }
            }
          }
        }
      };
      visit(rootId);
      return out;
    };

    // Render an RT prop body via the injected walker (one root per child).
    const renderRtBody = (childIds: string[]): string =>
      childIds.map((cid) => renderNodes(collectSubtree(cid))).join('');

    // Plain Text body: every child is a bare text/String leaf → `type="text"`.
    const isPlainTextBody = (childIds: string[]): boolean =>
      childIds.every((cid) => {
        const n = nodeIndex[cid];
        return Boolean(
          n &&
          (n.text || n.type === 'String') &&
          (!n.children || n.children.length === 0)
        );
      });

    const labelAttrFor = (propKey: string): string => {
      if (!annotate || !typeId) return '';
      const label = annotate.resolvePropLabel(typeId, propKey);
      return label ? ` label="${escWhtmlAttr(label)}"` : '';
    };

    const scalarAttrs: string[] = [];
    const childLines: string[] = [];

    for (const propKey of Object.keys(overrides)) {
      // Drop overrides for props no longer on the live definition (deleted after
      // save): emitting them would round-trip a stale `prop-*` the write path
      // now rejects. Only drops confirmed-missing props; no-op without the ctx.
      if (annotate && typeId && annotate.isKnownMissingProp(typeId, propKey)) {
        continue;
      }
      const value = overrides[propKey];
      const nameAttr = `name="${escWhtmlAttr(propKey)}"`;
      const labelAttr = labelAttrFor(propKey);

      if (Array.isArray(value)) {
        const childIds = value.filter(
          (v): v is string => typeof v === 'string' && v in nodeIndex
        );
        // A node-ref body is a non-empty array of id strings; Attributes are an
        // array of `{name, value}` objects. Discriminate by element shape so a
        // body whose ids are all stale/pruned isn't misread as an Attributes literal.
        const isNodeRefBody =
          value.length > 0 && value.every((v) => typeof v === 'string');
        if (isNodeRefBody && childIds.length === value.length) {
          // RT / Plain Text — array of child node references, all resolvable.
          const body = renderRtBody(childIds);
          const type = isPlainTextBody(childIds) ? 'text' : 'richtext';
          childLines.push(
            `<wf-prop ${nameAttr}${labelAttr} type="${type}">${body}</wf-prop>`
          );
        } else if (isNodeRefBody) {
          // String-shaped body with some/all ids missing from the index
          // (stale/pruned/malformed). Report rather than silently emitting it
          // as an attribute literal, which would corrupt the markup.
          onError?.({
            instanceId: instanceNode._id,
            message: `prop "${propKey}" references node ids missing from the index`,
          });
        } else {
          // Object-array literal (Attributes — List<CustomAttribute>).
          scalarAttrs.push(
            `prop-${escWhtmlAttr(propKey)}='${escWhtmlSingleQuotedAttr(
              JSON.stringify(value)
            )}'`
          );
        }
        continue;
      }

      if (typeof value === 'string') {
        // Scalar string (Plain Text, HeadingTag, or JSON-string e.g. Link).
        // String overrides are always literals, never nested-instance references.
        scalarAttrs.push(
          `prop-${escWhtmlAttr(propKey)}="${escWhtmlAttr(value)}"`
        );
        continue;
      }

      if (typeof value === 'number') {
        scalarAttrs.push(`prop-${escWhtmlAttr(propKey)}="${String(value)}"`);
        continue;
      }

      if (value !== null && typeof value === 'object') {
        const tag = (value as {tag?: unknown}).tag;
        if (tag === 'True' || tag === 'False') {
          // Boolean override — stored as a `{tag, val}` union.
          scalarAttrs.push(
            `prop-${escWhtmlAttr(propKey)}="${tag === 'True' ? 'true' : 'false'}"`
          );
        } else {
          // Other object literal (Image / Video / Record) → JSON-in-attr.
          scalarAttrs.push(
            `prop-${escWhtmlAttr(propKey)}='${escWhtmlSingleQuotedAttr(
              JSON.stringify(value)
            )}'`
          );
        }
        continue;
      }
      // null / undefined / bare boolean → no override on the wire.
    }

    const openAttrs =
      `data-w-id="${escWhtmlAttr(instanceNode._id)}" ` +
      `component-id="${escWhtmlAttr(typeId)}"` +
      componentNameAttr +
      scalarAttrs.map((a) => ` ${a}`).join('');

    if (childLines.length === 0) {
      return `<wf-component ${openAttrs}></wf-component>`;
    }
    return `<wf-component ${openAttrs}>${childLines.join('')}</wf-component>`;
  } catch (err) {
    onError?.({
      instanceId: instanceNode._id,
      message: err instanceof Error ? err.message : String(err),
      error: err,
    });
    // Fallback: empty `<wf-component>` keeping the instance identity so the
    // inbound parser still recognizes the placeholder.
    const fallbackTypeId = instanceNode.data?.sym?.inst ?? '';
    return `<wf-component data-w-id="${escWhtmlAttr(
      instanceNode._id
    )}" component-id="${escWhtmlAttr(fallbackTypeId)}"></wf-component>`;
  }
};

// Injected so this package never imports (and never cycles into) the heavy
// html-to-webflow / corePlugins server chains; only `import type` remains.
export type WhtmlParserDeps = {
  HTMLToWFDLParser: typeof HTMLToWFDLParserType;
  getBootstrappedPlugins: typeof GetBootstrappedPluginsType;
};

// Parse-side mirror of `renderNodes`: `HTMLToWFDLParser` has no RTE figure
// support, so a richtext prop body is parsed with this instead.
export type ParseRichTextBody = (html: string) => {
  jsonContentNodes: JSONNode[];
  rootChildIds: string[];
};

const WF_PROP_TAG = 'wf-prop';
const WF_COMPONENT_TAG = 'wf-component';
// Mirrors RTE_EMBED_TYPE_ATTR: an HtmlEmbed body is emitted as a
// `<div data-rt-embed-type>` whose raw content is inert — a literal
// `<wf-component>` inside it is not a component instance.
const RTE_EMBED_TYPE_ATTR = 'data-rt-embed-type';

// Re-escapes what htmlparser2 decoded (`decodeEntities: true`). Shared with
// `whtmlCaptureController`'s own re-serialization for the same reason.
export const serializeWhtmlAttrs = (attrs: Record<string, string>): string => {
  const parts: string[] = [];
  for (const [k, v] of Object.entries(attrs)) {
    parts.push(`${k}="${escWhtmlAttr(v)}"`);
  }
  return parts.length === 0 ? '' : ' ' + parts.join(' ');
};
export const serializeWhtmlText = (s: string): string =>
  s.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');

// Pulls each `<wf-prop type="richtext">` body out and replaces it with a
// marker, so the generic parser below never mangles figure/img content.
// Depth tracks `wf-prop` opens/closes only — nested pairs net to zero.
const extractRichTextPropBodies = (
  buffer: string
): {
  buffer: string;
  captures: Array<{
    marker: string;
    html: string;
    propName: string;
    hasNestedComponent: boolean;
  }>;
} => {
  if (!buffer.includes(`<${WF_PROP_TAG}`)) return {buffer, captures: []};

  const captures: Array<{
    marker: string;
    html: string;
    propName: string;
    hasNestedComponent: boolean;
  }> = [];
  let out = '';
  let captureDepth = 0;
  let captureBuffer = '';
  let captureMarker = '';
  let capturePropName = '';
  let captureHasNestedComponent = false;
  // Tag depth inside a `data-rt-embed-type` div; while > 0 a `<wf-component>`
  // is inert embed content, not a nested instance.
  let embedDepth = 0;

  const parser = new htmlparser2.Parser(
    {
      onopentag: (tag: string, attributes: Record<string, string>) => {
        const tagMarkup = `<${tag}${serializeWhtmlAttrs(attributes)}>`;
        // Mirrors html-to-wfdl-parser.ts's own default-to-text handling
        // (`attributes['type'] || 'text'`): anything other than exactly
        // `"text"` is richtext/list content there, so it must be extracted
        // here too, or a non-canonical type value reintroduces CONT-6123.
        if (
          captureDepth === 0 &&
          tag === WF_PROP_TAG &&
          (attributes['type'] || 'text') !== 'text'
        ) {
          captureDepth = 1;
          captureBuffer = '';
          capturePropName = attributes['name'] || '';
          captureHasNestedComponent = false;
          embedDepth = 0;
          captureMarker = `__cont6123_richtext_${GuidUtils.generateGuid()}__`;
          out += `${tagMarkup}${captureMarker}`;
          return;
        }
        if (captureDepth > 0) {
          captureBuffer += tagMarkup;
          if (tag === WF_PROP_TAG) captureDepth++;
          if (embedDepth > 0) {
            embedDepth++;
          } else if (
            // Match the canonical HtmlEmbed predicate exactly (JsonDomUtils):
            // only a `<div data-rt-embed-type="true">` is inert embed content.
            tag === 'div' &&
            attributes[RTE_EMBED_TYPE_ATTR] === 'true'
          ) {
            embedDepth = 1;
          } else if (tag === WF_COMPONENT_TAG) {
            captureHasNestedComponent = true;
          }
          return;
        }
        out += tagMarkup;
      },
      ontext: (text: string) => {
        const escaped = serializeWhtmlText(text);
        if (captureDepth > 0) captureBuffer += escaped;
        else out += escaped;
      },
      oncomment: (comment: string) => {
        const markup = `<!--${comment}-->`;
        if (captureDepth > 0) captureBuffer += markup;
        else out += markup;
      },
      // @ts-expect-error - htmlparser2's types omit onclosetag's tag arg.
      onclosetag: (tag) => {
        if (captureDepth > 0) {
          if (tag === WF_PROP_TAG) {
            captureDepth--;
            if (captureDepth === 0) {
              captures.push({
                marker: captureMarker,
                html: captureBuffer,
                propName: capturePropName,
                hasNestedComponent: captureHasNestedComponent,
              });
              out += `</${tag}>`;
              return;
            }
          }
          if (embedDepth > 0) embedDepth--;
          captureBuffer += `</${tag}>`;
          return;
        }
        out += `</${tag}>`;
      },
    },
    {decodeEntities: true, recognizeSelfClosing: true}
  );
  parser.write(buffer);
  parser.end();
  return {buffer: out, captures};
};

/** Rebuild a Component Instance subtree from a captured `<wf-component>` chunk via `HTMLToWFDLParser`. */
export function buildComponentInstanceJsonNodes(opts: {
  buffer: string;
  instanceId: string | null;
  components: Component.ComponentMap | undefined;
  parserDeps: WhtmlParserDeps;
  // Injected to avoid a cycle with `rteHtmlToJsonNodes`; see `extractRichTextPropBodies`.
  parseRichTextBody?: ParseRichTextBody;
  onError?: (info: {
    instanceId: string | null;
    markup: string;
    message: string;
    reason?:
      | 'forbidden-element'
      | 'unknown-prop'
      | 'prop-type-mismatch'
      | 'nested-component-in-prop';
    // Prop name for prop-level failures (unknown-prop / prop-type-mismatch /
    // nested-component-in-prop), so consumers can name the offending prop.
    propName?: string;
    // Which forbidden element a `forbidden-element` error refers to, so consumers
    // can show slot- vs collection-list-specific copy. Spans both the definition
    // governance case (a Collection List → 'DynamoWrapper') and populated slot
    // content ('Slot'); the latter isn't a `RichTextForbiddenElementType`.
    forbiddenElementType?: 'DynamoWrapper' | 'Slot';
  }) => void;
}): {nodes: JSONNode[]; childElementIds: string[]} {
  const {
    buffer,
    instanceId,
    components,
    parserDeps,
    parseRichTextBody,
    onError,
  } = opts;
  const nodes: JSONNode[] = [];
  const childElementIds: string[] = [];

  if (components == null) {
    onError?.({
      instanceId,
      markup: buffer,
      message: 'no component map available to resolve the component',
    });
    return {nodes, childElementIds};
  }
  // Safe against false positives from user-authored text: `buffer` is assembled by
  // `whtmlCaptureController`, which re-escapes every decoded text/attribute value
  // (`serializeWhtmlText`/`serializeWhtmlAttrs`) before appending it. A literal `<wf-slot`
  // substring can only land here when htmlparser2 itself tokenized an actual
  // `<wf-slot>` open tag from the wire input.
  const hasRawWfSlot = /<wf-slot\b/i.test(buffer);
  if (hasRawWfSlot) {
    onError?.({
      instanceId,
      markup: buffer,
      message: "Component slots can't be populated in Rich Text.",
      reason: 'forbidden-element',
      forbiddenElementType: 'Slot',
    });
  }
  try {
    const {buffer: strippedBuffer, captures} = parseRichTextBody
      ? extractRichTextPropBodies(buffer)
      : {
          buffer,
          captures: [] as Array<{
            marker: string;
            html: string;
            propName: string;
            hasNestedComponent: boolean;
          }>,
        };
    // Extraction hides a prop-nested instance from the parser's own
    // nested-component-in-prop guard; re-assert it here.
    for (const {propName, hasNestedComponent} of captures) {
      if (hasNestedComponent) {
        onError?.({
          instanceId,
          markup: buffer,
          message: `Component nested inside rich text property '${propName}' is not allowed`,
          reason: 'nested-component-in-prop',
          propName,
        });
      }
    }
    const {HTMLToWFDLParser, getBootstrappedPlugins} = parserDeps;
    const plugins = getBootstrappedPlugins();
    const whtmlParser = new HTMLToWFDLParser({
      enableComponents: true,
      componentMap: components,
      plugins,
      initialGuid: GuidUtils.generateGuid(),
      // CMS RTF payloads carry no CSS/fonts/variables/interactions.
      existingStylesImmutable: [],
      existingStylesMutable: [],
      existingVariablesImmutable: [],
      interactions: undefined,
      isLibraryComponent: false,
      getAsset: () => ({id: '', url: ''}),
      shortIdMap: {},
    });
    whtmlParser.write(strippedBuffer);
    const result = whtmlParser.end();
    // Surface prop overrides the parser couldn't apply (recorded on
    // `result.errors`) so the write path can 400 instead of silently dropping
    // them. Duck-typed on `code` to keep the `import type` boundary; the
    // sentinel's type narrows to the literal, so renaming the constant upstream
    // breaks compilation here.
    const propParseErrorCode: ComponentPropParseError['code'] =
      'component-prop-parse-error';
    for (const err of result.errors) {
      if ((err as {code?: unknown}).code === propParseErrorCode) {
        const propError = err as ComponentPropParseError;
        onError?.({
          instanceId,
          markup: buffer,
          message: propError.message,
          reason: propError.kind,
          propName: propError.propName,
        });
      }
    }
    const elements = result.value as ExpressionElement[];
    if (elements.length === 0) {
      throw new Error('WHTML parser returned no root element');
    }
    if (containsRichTextSlotOverride(elements, components) && !hasRawWfSlot) {
      onError?.({
        instanceId,
        markup: buffer,
        message: "Component slots can't be populated in Rich Text.",
        reason: 'forbidden-element',
        forbiddenElementType: 'Slot',
      });
      return {nodes, childElementIds};
    }
    // Identity preservation: restore the original `data-w-id` on the root (parser regenerates it) so PATCH targets the same instance; inner ids stay fresh.
    const preservedElements: ExpressionElement[] = elements.map((el, idx) =>
      idx === 0 && instanceId !== null ? {...el, id: instanceId} : el
    );
    let producedInstance = false;
    for (const elementExpr of preservedElements) {
      const parsedNodes = exportElementAsJSONNodeArray(components)(elementExpr);
      for (const node of parsedNodes) {
        nodes.push(node);
        // Inline component-instance check (importing `isComponentInstance`
        // from JsonDomUtils would cycle). `type === 'Symbol'` excludes
        // line-break nodes, which also carry `sym.inst` (e.g. `'LineBreak'`).
        if (node?.type === 'Symbol' && node?.data?.sym?.inst) {
          producedInstance = true;
        }
      }
      childElementIds.push(elementExpr.id);
    }
    // Splice each parsed richtext body back in, replacing its marker node.
    // Matching by marker (not prop name) still works when sibling/nested
    // instances share a prop's field id. Both indices below are built in a
    // single pass over `nodes` up front rather than rescanning per capture.
    if (captures.length > 0) {
      const captureMarkers = new Set(captures.map((c) => c.marker));
      const markerNodeIndexByMarker = new Map<string, number>();
      const overrideOwnerById = new Map<
        string,
        {overrides: Record<string, unknown>; propKey: string}
      >();
      nodes.forEach((node, index) => {
        // A marker survives as a bare text leaf — either shape this codebase
        // already treats as equivalent (see `isPlainTextBody` above).
        const leaf = node as unknown as {
          text?: boolean;
          type?: string;
          v?: unknown;
        };
        if (
          (leaf.text === true || leaf.type === 'String') &&
          typeof leaf.v === 'string' &&
          captureMarkers.has(leaf.v)
        ) {
          markerNodeIndexByMarker.set(leaf.v, index);
        }
        const overrides = (
          node as JSONNode & {
            data?: {sym?: {overrides?: Record<string, unknown>}};
          }
        ).data?.sym?.overrides;
        if (!overrides) return;
        for (const propKey of Object.keys(overrides)) {
          const value = overrides[propKey];
          if (!Array.isArray(value)) continue;
          for (const refId of value) {
            if (typeof refId === 'string') {
              overrideOwnerById.set(refId, {overrides, propKey});
            }
          }
        }
      });

      for (const {marker, html} of captures) {
        const markerIndex = markerNodeIndexByMarker.get(marker);
        if (markerIndex === undefined) {
          // The generic parser didn't preserve the marker verbatim — fail
          // loudly instead of silently dropping the prop's rich content.
          onError?.({
            instanceId,
            markup: buffer,
            message:
              'richtext prop content lost: marker node not found after parse',
          });
          continue;
        }
        const markerNode = nodes[markerIndex] as JSONNode & {_id: string};
        const owner = overrideOwnerById.get(markerNode._id);
        const {jsonContentNodes, rootChildIds} = parseRichTextBody!(html);
        if (owner) {
          owner.overrides[owner.propKey] = rootChildIds;
        } else {
          onError?.({
            instanceId,
            markup: buffer,
            message:
              'richtext prop content parsed but no override references its marker',
          });
        }
        nodes.push(...jsonContentNodes);
      }
      // Remove marker placeholder nodes now that overrides no longer reference them.
      const removedIndices = new Set(markerNodeIndexByMarker.values());
      if (removedIndices.size > 0) {
        for (let i = nodes.length - 1; i >= 0; i--) {
          if (removedIndices.has(i)) nodes.splice(i, 1);
        }
      }
    }
    // Unknown `component-id` doesn't throw — INSERT mode yields generic HTML with no `data.sym.inst`. Detect and report so the write path can reject with 400.
    if (!producedInstance) {
      onError?.({
        instanceId,
        markup: buffer,
        message: 'component-id did not resolve to a known component',
      });
    } else {
      // Component governance: a resolvable component can still be disallowed in
      // Rich Text if its definition contains a Collection List. This mirrors the
      // frontend rule in `packages/utilities/rich-text-validation`
      // (`ValidateNesting.ts`), which the Designer enforces on drag/drop but the
      // Data API write path bypasses entirely without this check.
      // Validate the definition so its slot defaults are included too.
      for (const elementExpr of preservedElements) {
        const forbidden = findRichTextForbiddenElementInComponent(
          elementExpr.type,
          components
        );
        if (forbidden) {
          onError?.({
            instanceId,
            markup: buffer,
            message: `contains ${getRichTextForbiddenElementDisplayName(forbidden)}, which is not allowed in a Rich Text field`,
            reason: 'forbidden-element',
            forbiddenElementType: forbidden,
          });
          break;
        }
      }
    }
  } catch (err) {
    onError?.({
      instanceId,
      markup: buffer,
      message: err instanceof Error ? err.message : String(err),
    });
  }
  return {nodes, childElementIds};
}
